import React, { useEffect, useState, useRef } from "react";
import IconButton from "components/common/iconButton";
import { Card, Col, Row, Spinner, Form, Nav, Tab, Button, OverlayTrigger, Tooltip, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import axios from "axios";
import Background from "components/common/background";
import corner4 from "assets/img/illustrations/corner-4.png";
import { useAppContext } from "../../providers/appProvider";
import Flex from "components/common/flex";
import AdvanceTable from "components/common/advance-table/advanceTable";
import AdvanceTableProvider from "providers/advanceTableProvider";
import AdvanceTableSearchBox from "components/common/advance-table/advanceTableSearchBox";
import AdvanceTableFooter from "components/common/advance-table/advanceTableFooter";
import useAdvanceTable from "hooks/useAdvanceTable";
import SubtleBadge from "components/common/subtleBadge";
import { getInitialPageSize, fetchRoleMappings, notifyLeaveDecision, fetchLeaveConflictMap } from "../../assets/js/common";
import moment from "moment";
import UnavailabilitySidepanel from "../calender/unavailabilitySidepanel";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from 'react-toastify';
import { faUmbrellaBeach, faBriefcaseMedical, faCalendarAlt, faEye, faCheckCircle, faTimesCircle, faChevronDown, faExclamationTriangle, faHourglassHalf } from "@fortawesome/free-solid-svg-icons";
import { useLocation } from "react-router-dom";
import ManualPaginationFooter from "components/common/advance-table/manualPaginationFooter";
import useServerAdvanceTable from "hooks/useServerAdvanceTable";
import useSocket from "../../hooks/useSocket";

function Absence() {
    const { t, i18n } = useTranslation();
    const { config } = useAppContext();

    const [unavailabilities, setUnavailabilities] = useState([]);
    const [conflictMap, setConflictMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [summaryLoading, setSummaryLoading] = useState(true);
    const isInitialLoad = useRef(true);
    const latestRequestId = useRef(0);
    const abortControllerRef = useRef(null);
    const [globalFilter, setGlobalFilter] = useState("");
    const [dateFilter, setDateFilter] = useState("all");
    const [typeFilter, setTypeFilter] = useState("all");
    const userId = localStorage.getItem("UserId");
    const isMedewerker = localStorage.getItem("isMedewerker") === "true";
    const isEmployeeView = sessionStorage.getItem("isEmployeeView") === "true";
    const isEmployeeMode = isMedewerker || isEmployeeView;
    const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
    const [activeTab, setActiveTab] = useState("all");
    const [showAll, setShowAll] = useState(false);
    const [sidePanelMode, setSidePanelMode] = useState("view");
    const [selectedUnavailability, setSelectedUnavailability] = useState(null);
    const [decisionLoading, setDecisionLoading] = useState(null);
    const [typeOpen, setTypeOpen] = useState(false);
    const [dateOpen, setDateOpen] = useState(false);
    const typeRef = useRef(null);
    const dateRef = useRef(null);
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const requestId = queryParams.get("unavailabilityId");

    const [openedFromEmail, setOpenedFromEmail] = useState(false);

    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(getInitialPageSize("absenceTable"));
    const [totalPages, setTotalPages] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [activeWidget, setActiveWidget] = useState("all");
    const [summaryCounts, setSummaryCounts] = useState({
        pending: 0,
        pendingConflicts: 0,
        conflicts: 0,
        approved: 0,
        rejected: 0
    });

    useEffect(() => {
        const timer = setTimeout(() => {
            const trimmed = globalFilter.trim();
            if (trimmed.length === 0 || trimmed.length >= 2) {
                setDebouncedSearch(trimmed);
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [globalFilter]);

    useEffect(() => {
        setPageIndex(0);
    }, [dateFilter, typeFilter, debouncedSearch, activeTab, activeWidget]);

    useEffect(() => {
        setShowAll(false);
    }, [activeTab, dateFilter, typeFilter, debouncedSearch]);

    useEffect(() => {
        const handler = (e) => {
            if (typeRef.current && !typeRef.current.contains(e.target)) setTypeOpen(false);
            if (dateRef.current && !dateRef.current.contains(e.target)) setDateOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const getDateRange = (filterOption) => {
        switch (filterOption) {
            case "currentWeek":
                return {
                    startDate: moment().startOf("isoWeek"),
                    endDate: moment().endOf("isoWeek"),
                };
            case "nextWeek":
                return {
                    startDate: moment().add(1, "week").startOf("isoWeek"),
                    endDate: moment().add(1, "week").endOf("isoWeek"),
                };
            case "currentMonth":
                return {
                    startDate: moment().startOf("month"),
                    endDate: moment().endOf("month"),
                };
            case "all":
            default:
                return {
                    startDate: moment(0),
                    endDate: moment().add(100, "years"),
                };
        }
    };

    const roleReverseMap = {
        "Admin": "adminText",
        "Beheerder": "adminText",
        "App Owner": "appOwnerText",
        "App Eigenaar": "appOwnerText",
        "Client": "clientText",
        "Klant": "clientText",
        "Employee": "employeeText",
        "Medewerker": "employeeText"
    };

    const enrichWithUserInfo = async (item, users) => {
        const employee = users.find((u) => u.UserId === item.UserId);
        const approver = users.find((u) => u.UserId === item.DecisionBy);

        const roleName = employee?.Role
            ? await fetchRoleMappings(employee.Role, t)
            : "Employee";

        const roleKey = roleReverseMap[roleName] || "employeeText";

        return {
            ...item,
            userName: employee?.Name || "Unknown",
            userRole: roleKey,
            userEmail: employee?.Email || "",
            avatarColor: getColorFromName(employee?.Name || "Unknown"),
            decisionByName: approver?.Name || null
        };
    };

    const fetchUnavailability = async () => {
        setLoading(true);

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;
        const requestId = ++latestRequestId.current;

        try {
            const customerId = localStorage.getItem("customerId");
            if (!customerId && !isEmployeeMode) return;
            const { startDate, endDate } = getDateRange(dateFilter);

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/userUnavailabilityRoute/unavailabilitiesPaginated`,
                {
                    params: {
                        ...(isEmployeeMode ? { userId } : { customerId }),
                        startDate: startDate.format("YYYY-MM-DD"),
                        endDate: endDate.format("YYYY-MM-DD"),
                        typeFilter,
                        statusFilter: isEmployeeMode
                            ? activeTab
                            : (activeWidget !== "all" ? activeWidget : undefined),
                        search: debouncedSearch,
                        page: pageIndex + 1,
                        pageSize,
                    },
                    headers: {
                        Authorization: process.env.REACT_APP_BasicAuthSecret
                    },
                    signal: controller.signal
                }
            );

            if (requestId !== latestRequestId.current) return;

            const sortedData = response.data.unavailabilities || [];
            setTotalPages(response.data.totalPages || 0);
            setTotalCount(response.data.totalCount || 0);

            if (!sortedData.length) {
                setUnavailabilities([]);
                return;
            }

            let users = response.data.users;
            if (!Array.isArray(users) || users.length === 0) {
                const userIds = [
                    ...new Set(
                        sortedData.flatMap((i) => [
                            i.UserId,
                            i.DecisionBy
                        ]).filter(Boolean)
                    )
                ];

                if (userIds.length) {
                    const usersResponse = await axios.post(
                        `${process.env.REACT_APP_API_URL}/userRoute/users/byIds`,
                        { userIds },
                        {
                            headers: {
                                Authorization: process.env.REACT_APP_BasicAuthSecret,
                            },
                        }
                    );
                    users = usersResponse.data || [];
                } else {
                    users = [];
                }
            }

            //  Merge + Role Mapping
            const mergedData = await Promise.all(
                sortedData.map((item) => enrichWithUserInfo(item, users))
            );

            if (requestId !== latestRequestId.current) return;
            setUnavailabilities(mergedData);

        } catch (error) {
            if (axios.isCancel(error) || error.name === "CanceledError") return;
            if (requestId === latestRequestId.current) {
                console.error("Error fetching unavailability:", error);
                setUnavailabilities([]);
            }
        } finally {
            if (requestId === latestRequestId.current) setLoading(false);
        }
    };

    const fetchSummaryCounts = async () => {
        setSummaryLoading(true);
        try {
            const customerId = localStorage.getItem("customerId");
            if (!customerId) return;

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/userUnavailabilityRoute/unavailabilitySummaryCounts`,
                {
                    params: { customerId },
                    headers: { Authorization: process.env.REACT_APP_BasicAuthSecret }
                }
            );

            setSummaryCounts(response.data);
        } catch (error) {
            console.error("Error fetching unavailability summary counts:", error);
        } finally {
            setSummaryLoading(false);
        }
    };

    const fetchConflictMapForAbsence = async () => {
        try {
            const customerId = localStorage.getItem("customerId");
            const startDate = moment().subtract(1, "year");
            const endDate = moment().add(1, "year");
            const map = await fetchLeaveConflictMap(
                customerId,
                startDate.format("YYYY-MM-DD"),
                endDate.format("YYYY-MM-DD")
            );
            setConflictMap(map);
        } catch (error) {
            console.error("Error fetching conflict map:", error);
            setConflictMap({});
        }
    };

    useEffect(() => {
        fetchUnavailability();
    }, [dateFilter, typeFilter, activeTab, activeWidget, debouncedSearch, pageIndex, pageSize]);

    useEffect(() => {
        fetchConflictMapForAbsence();
        if (!isEmployeeMode) fetchSummaryCounts();
    }, []);

    const isScreenLoading = isInitialLoad.current && (loading || (!isEmployeeMode && summaryLoading));

    useEffect(() => {
        if (isInitialLoad.current && !loading && !summaryLoading) {
            isInitialLoad.current = false;
        }
    }, [loading, summaryLoading]);

    useSocket('unavailability:updated', () => {
        if (!isEmployeeMode) fetchSummaryCounts();
        fetchConflictMapForAbsence();
        fetchUnavailability();
    });

    useSocket('orderDetail:updated', () => {
        if (!isEmployeeMode) fetchSummaryCounts();
        fetchConflictMapForAbsence();
        fetchUnavailability();
    });

    useSocket('orderDetail:deleted', () => {
        if (!isEmployeeMode) fetchSummaryCounts();
        fetchConflictMapForAbsence();
        fetchUnavailability();
    });

    useEffect(() => {
        const openFromEmailLink = async () => {
            if (!requestId || openedFromEmail) return;

            try {
                const customerId = localStorage.getItem("customerId");
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/userUnavailabilityRoute/unavailabilityById/${requestId}`,
                    {
                        params: isEmployeeMode ? { userId } : { customerId },
                        headers: { Authorization: process.env.REACT_APP_BasicAuthSecret }
                    }
                );

                const record = response.data;
                if (!record) return;

                const usersResponse = await axios.post(
                    `${process.env.REACT_APP_API_URL}/userRoute/users/byIds`,
                    { userIds: [record.UserId, record.DecisionBy].filter(Boolean) },
                    { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
                );

                const users = usersResponse.data || [];
                const enrichedRecord = await enrichWithUserInfo(record, users);

                setSelectedUnavailability(enrichedRecord);
                setSidePanelMode("view");
                setIsSidePanelOpen(true);
                setOpenedFromEmail(true);

                const params = new URLSearchParams(location.search);
                params.delete("unavailabilityId");
                const newSearch = params.toString();
                window.history.replaceState(
                    null,
                    "",
                    `${location.pathname}${newSearch ? `?${newSearch}` : ""}`
                );
            } catch (error) {
                console.error("Error opening unavailability from email link:", error);
            }
        };

        openFromEmailLink();
    }, [requestId]);

    const getInitials = (name) => {
        if (!name) return "";
        const parts = name.split(" ");
        return parts.map(p => p[0]).join("").substring(0, 2).toUpperCase();
    };

    const getTranslatedShortMonth = (date) => {
        const monthKey = moment(date).format("MMMM").toLowerCase();
        return t(`shortMonths.${monthKey}`);
    };

    const formatDateRange = (start, end) => {
        const startMoment = moment(start);
        const endMoment = moment(end);

        const startDay = startMoment.format("DD");
        const endDay = endMoment.format("DD");

        const startMonth = getTranslatedShortMonth(start);
        const endMonth = getTranslatedShortMonth(end);

        const year = endMoment.format("YYYY");

        if (startMoment.isSame(endMoment, "day")) {
            return `${startDay} ${startMonth} ${year}`;
        }

        return `${startDay} ${startMonth} — ${endDay} ${endMonth} ${year}`;
    };

    const calculateDuration = (start, end, startTime = null, endTime = null) => {
        if (startTime && endTime) {
            const startMoment = moment.utc(startTime);
            const endMoment = moment.utc(endTime);
            const diffHours = endMoment.diff(startMoment, "minutes") / 60;

            if (diffHours > 0 && diffHours < 8) {
                const hrs = Math.floor(diffHours);
                const mins = Math.round((diffHours - hrs) * 60);
                return mins > 0
                    ? `${hrs}${t("hours")} ${mins}${t("minutes")}`
                    : `${hrs}${t("hours")}`;
            }
        }

        const diffDays = Math.abs(new Date(end) - new Date(start)) / (1000 * 60 * 60 * 24) + 1;
        return t("day", { count: diffDays });
    };

    const handleKeyboardActivation = (event, handler) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handler();
        }
    };

    const handleKpiClick = (key) => {
        setActiveWidget(prev => (prev === key ? "all" : key));
    };

    const getColorFromName = (name) => {
        const colors = [
            "bg-primary-subtle text-primary",
            "bg-success-subtle text-success",
            "bg-warning-subtle text-warning",
            "bg-danger-subtle text-danger",
            "bg-info-subtle text-info",
            "bg-secondary-subtle text-secondary"
        ];

        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }

        return colors[Math.abs(hash) % colors.length];
    };

    const handleDecision = async (unavailabilityId, newStatus, note = null) => {
        setDecisionLoading(`${unavailabilityId}-${newStatus}`);
        try {
            const adminId = localStorage.getItem("UserId");
            const customerId = localStorage.getItem("customerId");
            const decisionDate = moment().format("YYYY-MM-DD");

            const payload = {
                status: newStatus,
                decisionOn: decisionDate,
                decisionBy: adminId,
                adminNote: note || null
            };

            const response = await axios.patch(
                `${process.env.REACT_APP_API_URL}/userUnavailabilityRoute/updateStatus/${unavailabilityId}`,
                payload,
                { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );

            if (response.status !== 200) {
                throw new Error("Update failed");
            }

            toast.success(
                newStatus === "approved" ? t("leaveApprovedSuccessfully") : t("leaveRejectedSuccessfully"),
                { position: "top-right", autoClose: 2000 }
            );

            await fetchUnavailability();
            fetchSummaryCounts();

            notifyLeaveDecision({
                unavailabilityId,
                updatedRecord: response.data,
                newStatus,
                note,
                adminId,
                customerId,
                t,
            }).catch(err => console.error("Leave decision notification error (non-blocking):", err));

        } catch (error) {
            console.error("Decision error:", error);
            toast.error(
                newStatus === "approved" ? t("failedToApproveLeave") : t("failedToRejectLeave"),
                { position: "top-right", autoClose: 2000 }
            );
        } finally {
            setDecisionLoading(null);
        }
    };

    const columns = [
        {
            accessorKey: "employee",
            header: t("employee"),
            cell: ({ row: { original } }) => {
                const colorClass = original.avatarColor;

                return (
                    <Flex alignItems="center" className="text-start">
                        <div
                            className={`avatar avatar-xl rounded-circle ${colorClass} d-flex align-items-center justify-content-center me-3`}
                        >
                            <span className="fw-bold">
                                {getInitials(original.userName)}
                            </span>
                        </div>

                        <div>
                            <div className="fw-semibold">{original.userName}</div>
                            <div className="fs-11 text-500">
                                {t(original.userRole)}
                            </div>
                        </div>
                    </Flex>
                );
            }
        },
        {
            accessorKey: "Type",
            header: t("type"),
            cell: ({ row: { original } }) => {
                const normalizedType = original.Type?.toLowerCase();

                const badgeColor =
                    normalizedType === "vacation"
                        ? "primary"
                        : normalizedType === "sickness"
                            ? "danger"
                            : "secondary";

                return (
                    <SubtleBadge pill bg={badgeColor}>
                        <p className="mb-0 data-font-size">
                            {t(normalizedType)}
                        </p>
                    </SubtleBadge>
                );
            }
        },
        {
            accessorKey: "dateRange",
            header: t("dateRange"),
            meta: {
                headerProps: { className: "date-range-col" },
                cellProps: { className: "date-range-col" }
            },
            cell: ({ row: { original } }) => {

                const hasConflict = Boolean(original.HasConflict);

                return (
                    <div className="d-flex flex-wrap flex-md-nowrap align-items-center gap-1">
                        <span className="text-nowrap">
                            {`${moment(original.StartDate).format("DD-MM-YYYY")} – ${moment(original.EndDate).format("DD-MM-YYYY")}`}
                        </span>
                        {hasConflict && (
                            <span className="badge badge-subtle-warning rounded-pill d-inline-flex align-items-center gap-1 ms-md-2 px-2 py-1 font-size-11 text-nowrap">
                                <FontAwesomeIcon icon={faExclamationTriangle} transform="shrink-2" className="fs--2" />
                                {t("conflict", "Conflict")}
                            </span>
                        )}
                    </div>
                );
            }
        },
        {
            accessorKey: "duration",
            header: t("duration"),
            cell: ({ row: { original } }) =>
                calculateDuration(original.StartDate, original.EndDate, original.StartTime, original.EndTime)
        },
        {
            accessorKey: "Status",
            header: t("status"),
            cell: ({ row: { original } }) => {
                const normalizedStatus = original.Status?.toLowerCase();

                const badgeColor =
                    normalizedStatus === "approved"
                        ? "success"
                        : normalizedStatus === "rejected"
                            ? "danger"
                            : "warning";

                return (
                    <SubtleBadge pill bg={badgeColor}>
                        <p className="mb-0 data-font-size">
                            {t(normalizedStatus)}
                        </p>
                    </SubtleBadge>
                );
            }
        },
        {
            id: "actions",
            header: t("actions"),
            cell: ({ row }) => {
                const original = row.original;
                const status = original.Status?.toLowerCase();

                const groupRequests = unavailabilities.filter(
                    r => r.RequestGroupId === original.RequestGroupId
                );

                const latestRequest = groupRequests.sort(
                    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
                )[0];

                const isLatestRequest =
                    latestRequest?.UnavailabilityId === original.UnavailabilityId;
                const showOnlyView = !isLatestRequest;

                return (
                    <div
                        className="d-flex gap-3 align-items-center"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* VIEW */}
                        {(!isLatestRequest || status === "approved" || status === "rejected") && (
                            <OverlayTrigger placement="top" overlay={<Tooltip>{t("view")}</Tooltip>}>
                                <FontAwesomeIcon
                                    icon={faEye}
                                    size="lg"
                                    className="text-primary cursor-pointer"
                                    onClick={() => {
                                        setSelectedUnavailability(original);
                                        setSidePanelMode("view");
                                        setIsSidePanelOpen(true);
                                    }}
                                />
                            </OverlayTrigger>
                        )}

                        {/* APPROVE */}
                        {isLatestRequest && (status === "pending" || status === "rejected") && (
                            <OverlayTrigger placement="top" overlay={<Tooltip>{t("approve")}</Tooltip>}>
                                {decisionLoading === `${original.UnavailabilityId}-approved` ? (
                                    <Spinner animation="border" size="sm" variant="success" />
                                ) : (
                                    <FontAwesomeIcon
                                        icon={faCheckCircle}
                                        size="lg"
                                        className="text-success cursor-pointer"
                                        onClick={() =>
                                            handleDecision(original.UnavailabilityId, "approved")
                                        }
                                    />
                                )}
                            </OverlayTrigger>
                        )}

                        {/* REJECT */}
                        {isLatestRequest && status === "pending" && (
                            <OverlayTrigger placement="top" overlay={<Tooltip>{t("reject")}</Tooltip>}>
                                {decisionLoading === `${original.UnavailabilityId}-rejected` ? (
                                    <Spinner animation="border" size="sm" variant="danger" />
                                ) : (
                                    <FontAwesomeIcon
                                        icon={faTimesCircle}
                                        size="lg"
                                        className="text-danger cursor-pointer"
                                        onClick={() =>
                                            handleDecision(original.UnavailabilityId, "rejected")
                                        }
                                    />
                                )}
                            </OverlayTrigger>
                        )}
                    </div>
                );
            }
        }
    ];

    const table = useServerAdvanceTable({
        data: unavailabilities,
        columns,
        selection: false,
        sortable: false,
        pagination: true,
        manualPagination: true,
        pageCount: totalPages,
        paginationState: { pageIndex, pageSize },
        onPaginationChange: (updater) => {
            const next = typeof updater === "function" ? updater({ pageIndex, pageSize }) : updater;
            setPageIndex(next.pageIndex);
            setPageSize(next.pageSize);
        },
    });

    const formatLocalTime = (dateStr, timeStr) => {
        if (!dateStr || !timeStr) return "--";

        const time = moment.utc(timeStr).format("HH:mm:ss");
        const combinedUtc = moment.utc(
            `${moment.utc(dateStr).format("YYYY-MM-DD")}T${time}`
        );
        return combinedUtc.local().format("HH:mm");
    };

    const getLocalTimeRange = (item) => {
        if (!item?.StartTime || !item?.EndTime) {
            return "--";
        }

        const startTime = formatLocalTime(item.StartDate, item.StartTime);
        const endTime = formatLocalTime(item.EndDate, item.EndTime);

        return `${startTime} – ${endTime}`;
    };

    const renderCard = (item) => {
        const isVacation = item.Type?.toLowerCase() === "vacation";

        return (
            <Col xs={12} md={6} lg={4} key={item.UnavailabilityId}>
                <Card
                    className="h-100  shadow-none card-hover border cursor-pointer"
                    onClick={() => {
                        setSelectedUnavailability(item);
                        setSidePanelMode("edit");
                        setIsSidePanelOpen(true);
                    }}
                >
                    <Card.Body>
                        {/* Header */}
                        <div className="d-flex justify-content-between align-items-start mb-3">
                            <div className="d-flex align-items-center gap-2">
                                <FontAwesomeIcon
                                    icon={isVacation ? faUmbrellaBeach : faBriefcaseMedical}
                                    className="text-primary"
                                />
                                <h6 className="fw-semibold fs-9 mb-0">
                                    <strong>{t(item.Type?.toLowerCase())}</strong>
                                </h6>
                            </div>

                            {
                                (() => {
                                    const normalizedStatus = item.Status?.toLowerCase();

                                    const badgeColor =
                                        normalizedStatus === "approved"
                                            ? "success"
                                            : normalizedStatus === "rejected"
                                                ? "danger"
                                                : "warning";

                                    return (
                                        <SubtleBadge pill bg={badgeColor}>
                                            {t(normalizedStatus)}
                                        </SubtleBadge>
                                    );
                                })()
                            }
                        </div>

                        {/* Date */}
                        <div className="d-flex align-items-center text-primary fs-10 fw-medium mb-1">
                            <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                            {`${moment(item.StartDate).format("DD-MM-YYYY")} - ${moment(item.EndDate).format("DD-MM-YYYY")}`}
                        </div>

                        <div className="text-muted font-size-12 mb-3 ms-3">
                            {getLocalTimeRange(item)}
                        </div>

                        {/* Notes Section */}
                        <div className="mt-3">
                            <div className="bg-100 border-start border-3 border-success rounded-2 px-3 py-2">
                                <div className="d-flex justify-content-start">
                                    <div
                                        className="text-muted fs-10 fw-100"
                                    >
                                        {t("notes")}
                                    </div>
                                </div>
                                <div className="mt-1">
                                    <p className="text-muted d-flex align-items-center gap-1 mb-0 fw-normal font-size-12">
                                        {item.Notes && item.Notes.trim() !== ""
                                            ? `${item.Notes}`
                                            : t("noNotesAvailable")}
                                    </p>
                                </div>

                            </div>
                        </div>
                    </Card.Body>
                </Card>
            </Col>
        );
    };

    const getLimitedData = (data) => (showAll ? data : data.slice(0, 6));

    const hasViewAll = unavailabilities.length > 6;

    const renderEmployeeTiles = () => {
        if (loading) {
            return (
                <div className="d-flex justify-content-center align-items-center py-5">
                    <Spinner animation="border" />
                </div>
            );
        }

        if (unavailabilities.length === 0) {
            return (
                <div className="d-flex justify-content-center align-items-center py-5 text-muted">
                    {t("noDataAvailable")}
                </div>
            );
        }

        return (
            <>
                <Row className="g-3">
                    {getLimitedData(unavailabilities).map(renderCard)}
                </Row>

                {hasViewAll && (
                    <div className="text-center mt-3">
                        <button
                            type="button"
                            onClick={() => setShowAll(prev => !prev)}
                            className="btn p-0 d-inline-block px-3 py-2 rounded view-inventory-link shadow-none text-decoration-none fw-medium font-size-12"
                        >
                            {showAll ? t("showLess") : t("viewAll")}
                        </button>
                    </div>
                )}
            </>
        );
    };

    const renderEmployeePagination = () => {
        if (totalPages <= 1) return null;

        const rangeStart = pageIndex * pageSize + 1;
        const rangeEnd = Math.min((pageIndex + 1) * pageSize, totalCount);
        const isFirst = pageIndex === 0;
        const isLast = pageIndex >= totalPages - 1;

        return (
            <Card.Footer>
                <Flex className="align-items-center justify-content-between">
                    <Flex alignItems="center" className="fs-10">
                        <p className="mb-0">
                            <span className="d-none d-sm-inline-block me-2">
                                {rangeStart} {t("toLable")} {rangeEnd} {t("of")} {totalCount}
                            </span>
                        </p>
                    </Flex>
                    <Flex alignItems="center" justifyContent="center" className="flex-wrap gap-2 pagination-controls">
                        <Button
                            size="sm"
                            variant={isFirst ? "tertiary" : "primary"}
                            className={isFirst ? "disabled" : ""}
                            disabled={isFirst}
                            onClick={() => setPageIndex(prev => Math.max(prev - 1, 0))}
                        >
                            {t("previous")}
                        </Button>
                        <Button
                            size="sm"
                            variant={isLast ? "tertiary" : "primary"}
                            className={`px-4 ms-2 ${isLast ? "disabled" : ""}`}
                            disabled={isLast}
                            onClick={() => setPageIndex(prev => Math.min(prev + 1, totalPages - 1))}
                        >
                            {t("nextLable")}
                        </Button>
                    </Flex>
                </Flex>
            </Card.Footer>
        );
    };

    return (
        <>
            <AdvanceTableProvider {...table}>
                <Row className="g-3 mb-3">
                    <Col lg={12} xxl={12}>
                        <Card className="mb-3 h-100">
                            <Background image={corner4} className="bg-card" />
                            <Card.Body className="position-relative pe-3 pb-2">

                                <Row className="flex-between-center">
                                    <Col>
                                        {/* <h5 className="mb-2 mb-md-0">{t("menu.absence")}</h5> */}
                                        <h5 className="mb-2 mb-md-0">{isEmployeeMode ? t("menu.myLeaves") : t("menu.leaveManagement")}</h5>
                                    </Col>
                                    <Col xs="auto">
                                        {isEmployeeMode && (
                                            <IconButton
                                                variant="primary"
                                                size="sm"
                                                icon="plus"
                                                transform="shrink-3"
                                                onClick={() => {
                                                    setSelectedUnavailability(null);
                                                    setSidePanelMode("register");
                                                    setIsSidePanelOpen(true);
                                                }}
                                            >
                                                <span className="ms-1">{t("requestTimeOff")}</span>
                                            </IconButton>
                                        )}
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {isScreenLoading ? (
                    <div className="d-flex justify-content-center align-items-center py-5">
                        <Spinner animation="border" />
                    </div>
                ) : (
                <>
                {!isEmployeeMode && (
                    <Row className="g-3 mb-3">
                        {[
                            [
                                "pending",
                                t("pendingRequestsLabel"),
                                "warning",
                                "warning",
                                faHourglassHalf,
                                `${t("review", "Review")} →`,
                                t("pendingRequestsSubtext", {
                                    conflicts: summaryCounts.pendingConflicts || 0,
                                    total: summaryCounts.pending || 0,
                                    defaultValue: `${summaryCounts.pendingConflicts || 0} of ${summaryCounts.pending || 0} overlap existing shifts`
                                })
                            ],
                            [
                                "conflicts",
                                t("planningConflictsLabel"),
                                "danger",
                                "danger",
                                faExclamationTriangle,
                                `${t("resolve", "Resolve")} →`,
                                t("planningConflictsSubtext", "Review before approving these")
                            ],
                            [
                                "approved",
                                t("approvedRequestsLabel"),
                                "success",
                                "success",
                                faCheckCircle,
                                `${t("view", "View")} →`,
                                t("approvedRequestsSubtext", "Confirmed leave")
                            ],
                            [
                                "rejected",
                                t("rejectedRequestsLabel"),
                                "danger",
                                "danger",
                                faTimesCircle,
                                `${t("view", "View")} →`,
                                t("rejectedRequestsSubtext", "Declined leave")
                            ]
                        ].map(([key, label, color, badgeColor, icon, actionLabel, subtext]) => (
                            <Col xs={6} sm={6} xl={3} key={key}>
                                <Card
                                    className={`absence-kpi-card absence-kpi-border-${color} h-100 flex-grow-0 rounded-3 card-hover border cursor-pointer ${activeWidget === key ? `absence-kpi-active absence-kpi-selected-${color}` : "shadow-none"}`}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => handleKpiClick(key)}
                                    onKeyDown={(event) => handleKeyboardActivation(event, () => handleKpiClick(key))}
                                >
                                    <Card.Body className="py-3 px-3">
                                        <div className="mb-2">
                                            <div className={`d-flex align-items-center justify-content-center rounded-2 stat-icon bg-${badgeColor}-subtle`}>
                                                <FontAwesomeIcon icon={icon} className={`fs-8 text-${color}`} />
                                            </div>
                                        </div>

                                        <div className="mb-1 fw-bolder font-size-22">
                                            {summaryCounts[key] || 0}
                                        </div>

                                        <span className="mb-1 fw-semibold text-900 font-size-13 d-block">{label}</span>

                                        <div className="d-flex justify-content-between align-items-center gap-2">
                                            <div className="font-size-12 text-600 fw-normal">
                                                {subtext}
                                            </div>
                                            <div className={`p-0 d-inline-block text-decoration-none fw-medium font-size-12 text-${color} text-nowrap`}>
                                                {actionLabel}
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                )}

                <Card className="mb-3">
                    <Card.Header className={isEmployeeMode ? "border-bottom pb-2" : ""}>
                        <Row className="align-items-center">
                            <Col xs={12} className="d-flex justify-content-between align-items-center">
                                {isEmployeeMode && (
                                    <h5>
                                        {t("allRequests")}
                                    </h5>
                                )}

                                {!isEmployeeMode && (
                                    <div className="w-100 d-flex flex-column-reverse flex-md-row justify-content-md-between align-items-end align-items-md-center gap-2">

                                        <div className="w-md-auto d-flex justify-content-end justify-content-md-start">
                                            <AdvanceTableSearchBox setGlobalFilter={setGlobalFilter} />
                                        </div>

                                        <div className="d-flex flex-row flex-wrap justify-content-end align-items-center gap-2 ms-auto">

                                            {activeWidget !== "all" && (
                                                <span className="badge rounded-pill fw-medium font-size-11 badge-subtle-primary d-flex align-items-center gap-1 px-2 py-1">
                                                    {`${t("filtered", "Filtered")}: ${activeWidget === "conflicts" ? t("planningConflictsLabel") : t(`${activeWidget}RequestsLabel`)}`}
                                                    <button
                                                        type="button"
                                                        className="btn-close p-0"
                                                        aria-label={t("clear")}
                                                        onClick={() => setActiveWidget("all")}
                                                    />
                                                </span>
                                            )}


                                        <div className="d-flex align-items-center input-group-sm filter-group border rounded position-relative w-auto" ref={typeRef}>
                                            <span className="px-2 bg-light border-end text-900 fw-normal fs--1">
                                                {t("type")}:
                                            </span>
                                            <button
                                                type="button"
                                                className="btn border-0 bg-transparent d-flex align-items-center justify-content-between px-2 text-900 fs--1 fw-normal filter-dropdown-btn"
                                                onClick={() => setTypeOpen(prev => !prev)}
                                            >
                                                {typeFilter === "all" ? t("allLabel") : t(typeFilter)}
                                                <FontAwesomeIcon icon={faChevronDown} size="xs" className="ms-2 me-1 text-800 fw-normal" />
                                            </button>
                                            {typeOpen && (
                                                <div className="dropdown-menu dropdown-menu-falcon show shadow-sm py-1 filter-dropdown-menu">
                                                    {[["all", t("allLabel")], ["vacation", t("vacation")], ["sickness", t("sickness")]].map(([val, label]) => (
                                                        <button
                                                            key={val}
                                                            type="button"
                                                            className="dropdown-item animation-none fs--1 filter-dropdown-item"
                                                            onClick={() => { setTypeFilter(val); setTypeOpen(false); }}
                                                        >
                                                            {label}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {/* Date filter */}
                                        <div className="d-flex align-items-center input-group-sm filter-group border rounded position-relative w-auto" ref={dateRef}>
                                            <span className="px-2 bg-light border-end">
                                                <FontAwesomeIcon icon={faCalendarAlt} className="text-600" />
                                            </span>
                                            <button
                                                type="button"
                                                className="btn border-0 bg-transparent d-flex align-items-center justify-content-between px-2 text-900 fs--1 fw-normal filter-dropdown-btn"
                                                onClick={() => setDateOpen(prev => !prev)}
                                            >
                                                {dateFilter === "all" ? t("allLabel") : t(dateFilter)}
                                                <FontAwesomeIcon icon={faChevronDown} size="xs" className="ms-2 me-1 text-800 fw-normal" />
                                            </button>
                                            {dateOpen && (
                                                <div className="dropdown-menu dropdown-menu-falcon show shadow-sm py-1 filter-dropdown-menu">
                                                    {[["all", t("allLabel")], ["currentWeek", t("currentWeek")], ["nextWeek", t("nextWeek")], ["currentMonth", t("currentMonth")]].map(([val, label]) => (
                                                        <button
                                                            key={val}
                                                            type="button"
                                                            className="dropdown-item animation-none fs--1 filter-dropdown-item"
                                                            onClick={() => { setDateFilter(val); setDateOpen(false); }}
                                                        >
                                                            {label}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                            </Col>
                        </Row>
                    </Card.Header>

                    <Card.Body className="p-3">
                        {isEmployeeMode ? (
                            <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
                                <Nav variant="tabs" className="mb-3 border-bottom-0">
                                    <Nav.Item>
                                        <Nav.Link eventKey="all">
                                            {t("allLabel")}
                                        </Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link eventKey="pending">
                                            {t("pending")}
                                        </Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link eventKey="history">
                                            {t("history")}
                                        </Nav.Link>
                                    </Nav.Item>
                                </Nav>

                                <Tab.Content>
                                    <Tab.Pane eventKey="all">{renderEmployeeTiles()}</Tab.Pane>
                                    <Tab.Pane eventKey="pending">{renderEmployeeTiles()}</Tab.Pane>
                                    <Tab.Pane eventKey="history">{renderEmployeeTiles()}</Tab.Pane>
                                </Tab.Content>
                            </Tab.Container>
                        ) : (
                            <>
                                {loading ? (
                                    <div className="d-flex justify-content-center align-items-center py-5">
                                        <Spinner animation="border" />
                                    </div>
                                ) : unavailabilities.length === 0 ? (
                                    <div className="d-flex justify-content-center align-items-center py-5 text-muted">
                                        {t("noDataAvailable")}
                                    </div>
                                ) : (
                                    <AdvanceTable
                                        headerClassName="bg-200 text-nowrap align-middle text-start"
                                        rowClassName="align-middle text-start cursor-pointer"
                                        tableProps={{
                                            bordered: false,
                                            striped: false,
                                            size: "sm",
                                            className:
                                                "mb-0 overflow-hidden data-font-size table-hover"
                                        }}
                                        getRowProps={(row) => ({
                                            onClick: () => {
                                                setSelectedUnavailability(row.original);
                                                setSidePanelMode("edit");
                                                setIsSidePanelOpen(true);
                                            }
                                        })}
                                    />
                                )}
                            </>
                        )}
                    </Card.Body>

                    {isEmployeeMode && (showAll || !hasViewAll) && renderEmployeePagination()}
                    {!isEmployeeMode && (
                        <Card.Footer>
                            <ManualPaginationFooter
                                totalCount={totalCount}
                                pageIndex={pageIndex}
                                pageSize={pageSize}
                                tableKey="absenceTable"
                                onPageSizeChange={(newSize) => {
                                    setPageSize(newSize);
                                    setPageIndex(0);
                                }}
                            />
                        </Card.Footer>
                    )}
                </Card>
                </>
                )}
            </AdvanceTableProvider>
            <UnavailabilitySidepanel
                open={isSidePanelOpen}
                onClose={() => setIsSidePanelOpen(false)}
                data={selectedUnavailability}
                mode={sidePanelMode}
                t={t}
                onUpdated={fetchUnavailability}
                onDecision={handleDecision}
                decisionLoading={decisionLoading}
                conflictMap={conflictMap} 
            />
        </>
    );
}

export default Absence;